/**
 * Adaptateur Supabase des accès de suivi client.
 *
 * L'ouverture passe par `open_client_access()` et non par un `insert` : la
 * génération du jeton et le calcul de son échéance vivent en base, pour que ni
 * l'écran, ni l'agent, ni un appel direct ne puissent s'accorder un lien plus
 * long ou plus faible. Ce dépôt ne voit jamais le jeton autrement qu'en
 * retour, une fois.
 */
import type { ClientAccess, ExecutionContext, PortalRepository } from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const COLUMNS =
  "id, project_id, full_name, email, expires_at, revoked_at, last_seen_at, view_count, created_at";

function toAccess(row: Row): ClientAccess {
  return {
    id: row.id,
    projectId: row.project_id,
    fullName: row.full_name ?? null,
    email: row.email ?? null,
    expiresAt: row.expires_at,
    revokedAt: row.revoked_at ?? null,
    lastSeenAt: row.last_seen_at ?? null,
    viewCount: Number(row.view_count ?? 0),
    createdAt: row.created_at,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabasePortalRepository(
  clientFor: DbClientFactory,
): PortalRepository {
  return {
    async open(ctx: ExecutionContext, cmd): Promise<string> {
      const { data, error } = await clientFor(ctx).rpc("open_client_access", {
        p_project_id: cmd.projectId,
        p_full_name: cmd.fullName,
        p_email: cmd.email,
      });
      if (error) fail(error);
      return data as string;
    },

    async list(ctx: ExecutionContext, filter): Promise<ClientAccess[]> {
      let query = clientFor(ctx)
        .from("client_contacts")
        .select(COLUMNS)
        .order("created_at", { ascending: false });

      if (filter.projectId) query = query.eq("project_id", filter.projectId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toAccess);
    },

    async revoke(ctx: ExecutionContext, accessId: string): Promise<void> {
      // Marqué, jamais supprimé : savoir qu'un lien a existé, à qui, et
      // combien de fois il a été ouvert vaut mieux qu'une ligne effacée.
      const { error } = await clientFor(ctx)
        .from("client_contacts")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", accessId);
      if (error) fail(error);
    },
  };
}
